import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AudioButton, ChoiceButton, ExerciseCard, Muted, Stack } from '@fluent/ui';
import type { ListenChoose as ListenChooseExercise } from '@fluent/types';
import { useAudio } from '../audio/audioPlayer.js';
import './ChooseCorrect.css';

export interface ListenChooseProps {
  exercise: ListenChooseExercise;
  /** Display order for the options — defaults to authored order. */
  options?: readonly string[];
  selected: string | null;
  onSelectedChange: (next: string) => void;
  locked: boolean;
  lastVerdict: 'exact' | 'wrong' | 'close' | null;
}

/**
 * ListenChoose — audio-prompted multiple choice. The audio is the prompt;
 * the user picks the matching English from 2–4 options. Lower-stakes than
 * `listen_type` (no typing) — useful for early-lesson recognition before we
 * ask for production.
 *
 * Auto-plays the prompt audio once on mount; user can replay via the audio
 * button. After submit, the optional `translation_ka` is shown so the
 * learner can confirm the meaning.
 */
export function ListenChoose({
  exercise,
  options,
  selected,
  onSelectedChange,
  locked,
  lastVerdict,
}: ListenChooseProps) {
  const { t } = useTranslation();
  const audio = useAudio(exercise.audio_key);
  const autoPlayedRef = useRef<string | null>(null);

  useEffect(() => {
    if (autoPlayedRef.current === exercise.id) return;
    autoPlayedRef.current = exercise.id;
    audio.play();
  }, [exercise.id, audio]);

  return (
    <ExerciseCard kicker={t('lesson.listen_choose.kicker')}>
      <div className="fluent-choose__audio-row">
        <AudioButton
          size="lg"
          playing={audio.playing}
          onClick={() => (audio.playing ? audio.stop() : audio.play())}
          label={t('lesson.listen_choose.audio_aria')}
        />
        
      </div>
      <Stack gap={2} className="fluent-choose__options">
        {(options ?? exercise.options).map((opt, idx) => {
          const isSelected = selected === opt;
          const isAnswer = opt === exercise.answer;
          const state =
            lastVerdict == null
              ? 'idle'
              : isAnswer
                ? 'correct'
                : isSelected
                  ? 'wrong'
                  : 'idle';
          return (
            <ChoiceButton
              key={opt}
              lang="en"
              bullet={String.fromCharCode(65 + idx)}
              selected={isSelected}
              state={state}
              disabled={locked}
              onClick={() => onSelectedChange(opt)}
            >
              {opt}
            </ChoiceButton>
          );
        })}
      </Stack>
      {lastVerdict != null && exercise.translation_ka && (
        <div className="fluent-choose__translation">
          <Muted>{exercise.translation_ka}</Muted>
        </div>
      )}
    </ExerciseCard>
  );
}
